import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Menu, X } from "lucide-react";

export function PublicNav({ onOpenLead }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const links = [
    { to: "/", label: "Home" },
    { to: "/pricing", label: "Pricing" },
    { to: "/oral-champion", label: "Oral Champion" },
    { to: "/train-the-trainer", label: "Train the Trainer" },
    { to: "/faq", label: "FAQ" },
  ];
  return (
    <header className="sticky top-0 z-50 bg-cream/90 backdrop-blur-md border-b border-cream-deep">
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5">
          <span className="h-9 w-9 rounded-lg bg-brand text-white grid place-items-center font-heading font-bold">K</span>
          <span className="font-heading font-bold text-brand text-lg leading-tight">KOHC<span className="text-coral">.</span></span>
        </Link>
        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/ /g, "-")}`}
              className="text-sm font-medium text-foreground/80 hover:text-brand transition-colors">{l.label}</Link>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <Button data-testid="nav-dashboard-btn" onClick={() => nav("/app")} className="rounded-full bg-brand hover:bg-brand-hover text-white">Go to dashboard</Button>
          ) : (
            <>
              <Button data-testid="nav-login-btn" variant="ghost" onClick={() => nav("/login")} className="text-brand hover:text-brand-hover">Log in</Button>
              <Button data-testid="nav-trial-btn" onClick={() => nav("/signup")} className="rounded-full bg-coral hover:bg-coral-hover text-white">Start free trial</Button>
            </>
          )}
        </div>
        <button data-testid="nav-mobile-toggle" className="md:hidden text-brand" onClick={() => setOpen(!open)}>
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-cream-deep bg-cream px-5 py-4 flex flex-col gap-3">
          {links.map((l) => <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="text-sm font-medium py-1">{l.label}</Link>)}
          {user ? <Button onClick={() => nav("/app")} className="bg-brand text-white rounded-full">Dashboard</Button> : (
            <>
              <Button variant="outline" onClick={() => nav("/login")} className="rounded-full">Log in</Button>
              <Button onClick={() => nav("/signup")} className="bg-coral text-white rounded-full">Start free trial</Button>
            </>
          )}
        </div>
      )}
    </header>
  );
}

export function PublicFooter() {
  return (
    <footer className="bg-brand text-white/80 mt-24">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-14 grid md:grid-cols-4 gap-10">
        <div>
          <div className="flex items-center gap-2.5 mb-4">
            <span className="h-9 w-9 rounded-lg bg-white text-brand grid place-items-center font-heading font-bold">K</span>
            <span className="font-heading font-bold text-white text-lg">KOHC</span>
          </div>
          <p className="text-sm leading-relaxed text-white/70">One oral-health system across your entire organisation. Train your team, evidence your compliance, improve resident oral care.</p>
        </div>
        <div>
          <div className="label-caps text-white/50 mb-4">Platform</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/pricing" className="hover:text-white">Pricing</Link></li>
            <li><Link to="/signup" className="hover:text-white">Free trial</Link></li>
            <li><Link to="/train-the-trainer" className="hover:text-white">Train the Trainer</Link></li>
            <li><Link to="/verify" data-testid="footer-verify-link" className="hover:text-white">Verify a certificate</Link></li>
          </ul>
        </div>
        <div>
          <div className="label-caps text-white/50 mb-4">Community</div>
          <ul className="space-y-2 text-sm">
            <li><a href="https://www.facebook.com/groups/27941660172109456" target="_blank" rel="noreferrer" data-testid="footer-facebook" className="hover:text-white">Facebook group</a></li>
          </ul>
        </div>
        <div>
          <div className="label-caps text-white/50 mb-4">Contact</div>
          <p className="text-sm"><a href="mailto:info@kohc.co.uk" data-testid="footer-email" className="hover:text-white">info@kohc.co.uk</a></p>
          <p className="text-sm mt-1"><a href="tel:+441243710119" data-testid="footer-phone" className="hover:text-white">01243 710119</a></p>
        </div>
      </div>
      <div className="border-t border-white/10 py-5 text-center text-xs text-white/50" data-testid="site-footer-copyright">
        © 2026 KOHC. All rights reserved. · Aligned & supportive of CQC oral health standards
      </div>
    </footer>
  );
}
